import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Container, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  Select, Input, Button, Flex, Text, Spinner, Card, CardBody, useToast,
  Stat, StatLabel, StatNumber, StatGroup, InputGroup, InputLeftElement,
  IconButton, VStack,
} from '@chakra-ui/react';
import { FiSearch, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

const STATUS_COLORS = {
  paid: 'green',
  pending: 'yellow',
  waiting_payment: 'yellow',
  processing: 'blue',
  failed: 'red',
  declined: 'red',
  canceled: 'gray',
  expired: 'gray',
  refunded: 'purple',
  chargedback: 'orange',
};

const STATUS_LABELS = {
  paid: 'Pago',
  pending: 'Pendente',
  waiting_payment: 'Aguardando',
  processing: 'Processando',
  failed: 'Falhou',
  declined: 'Recusado',
  canceled: 'Cancelado',
  expired: 'Expirado',
  refunded: 'Reembolsado',
  chargedback: 'Contestado',
};

const METHOD_LABELS = {
  credit_card: 'Cartão',
  pix: 'PIX',
  boleto: 'Boleto',
};

const PaymentList = () => {
  const toast = useToast();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const params = new URLSearchParams({ page, limit: 15 });
      if (filters.status) params.set('status', filters.status);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);

      const res = await fetch(`${API_BASE_URL}api/payment/admin/history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Falha ao buscar pagamentos');
      const data = await res.json();
      setPayments(data.payments || []);
      setPagination(data.pagination || { page: 1, total: 0, totalPages: 1 });
      setStats(data.stats || null);
    } catch (err) {
      toast({ title: 'Erro', description: err.message, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
    } finally {
      setLoading(false);
    }
  }, [page, filters, toast]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Build chart data from payments
  const chartData = React.useMemo(() => {
    const byMonth = {};
    payments.forEach(p => {
      if (p.status !== 'paid') return;
      const d = new Date(p.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      byMonth[key] = (byMonth[key] || 0) + (p.amount || 0);
    });
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, total]) => ({ month, total: parseFloat(total.toFixed(2)) }));
  }, [payments]);

  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Pagamentos</Heading>
        <Button leftIcon={<FiRefreshCw />} size="sm" onClick={fetchPayments} isLoading={loading}>
          Atualizar
        </Button>
      </Flex>

      {/* Stats Cards */}
      {stats && (
        <StatGroup mb={6}>
          <Card flex={1} mr={3}><CardBody py={3}>
            <Stat><StatLabel>Total Recebido</StatLabel><StatNumber fontSize="lg" color="green.500">{formatCurrency(stats.totalAmount / 100)}</StatNumber></Stat>
          </CardBody></Card>
          <Card flex={1} mr={3}><CardBody py={3}>
            <Stat><StatLabel>Pagamentos</StatLabel><StatNumber fontSize="lg">{stats.totalPayments}</StatNumber></Stat>
          </CardBody></Card>
          <Card flex={1} mr={3}><CardBody py={3}>
            <Stat><StatLabel>Ticket Médio</StatLabel><StatNumber fontSize="lg">{formatCurrency(stats.avgAmount / 100)}</StatNumber></Stat>
          </CardBody></Card>
          <Card flex={1}><CardBody py={3}>
            <Stat><StatLabel>Total na Página</StatLabel><StatNumber fontSize="lg">{pagination.total}</StatNumber></Stat>
          </CardBody></Card>
        </StatGroup>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <Card mb={6}>
          <CardBody>
            <Heading size="sm" mb={3}>Receita por Mês</Heading>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `R$${v}`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="total" name="Receita" fill="#38A169" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}

      {/* Filters */}
      <Card mb={4}>
        <CardBody py={3}>
          <HStack spacing={3} wrap="wrap">
            <Select
              size="sm" maxW="180px" placeholder="Todos os status"
              value={filters.status}
              onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
            >
              <option value="paid">Pago</option>
              <option value="pending">Pendente</option>
              <option value="failed">Falhou</option>
              <option value="refunded">Reembolsado</option>
              <option value="chargedback">Contestado</option>
              <option value="canceled">Cancelado</option>
            </Select>
            <InputGroup size="sm" maxW="180px">
              <InputLeftElement pointerEvents="none" fontSize="xs" color="gray.400">De</InputLeftElement>
              <Input type="date" pl="8" value={filters.startDate} onChange={(e) => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(1); }} />
            </InputGroup>
            <InputGroup size="sm" maxW="180px">
              <InputLeftElement pointerEvents="none" fontSize="xs" color="gray.400">Até</InputLeftElement>
              <Input type="date" pl="8" value={filters.endDate} onChange={(e) => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(1); }} />
            </InputGroup>
            {(filters.status || filters.startDate || filters.endDate) && (
              <Button size="sm" variant="ghost" onClick={() => { setFilters({ status: '', startDate: '', endDate: '' }); setPage(1); }}>
                Limpar
              </Button>
            )}
          </HStack>
        </CardBody>
      </Card>

      {/* Table */}
      <Card>
        <CardBody p={0}>
          {loading ? (
            <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
          ) : payments.length === 0 ? (
            <Flex justify="center" py={10}><Text color="gray.500">Nenhum pagamento encontrado</Text></Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Data</Th>
                    <Th>Descrição</Th>
                    <Th>Método</Th>
                    <Th isNumeric>Valor</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payments.map((p) => (
                    <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                      <Td whiteSpace="nowrap" fontSize="sm">{formatDate(p.createdAt)}</Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">{p.description}</Text>
                          {p.plan && <Text fontSize="xs" color="gray.500">{p.plan.name}</Text>}
                        </VStack>
                      </Td>
                      <Td>
                        <Badge variant="subtle" fontSize="xs">{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</Badge>
                        {p.card && <Text fontSize="xs" color="gray.500">****{p.card.lastFourDigits}</Text>}
                      </Td>
                      <Td isNumeric fontWeight="medium" fontSize="sm">{formatCurrency(p.amount)}</Td>
                      <Td>
                        <Badge colorScheme={STATUS_COLORS[p.status] || 'gray'} fontSize="xs">
                          {STATUS_LABELS[p.status] || p.status}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Flex justify="space-between" align="center" p={3} borderTop="1px solid" borderColor="gray.100">
              <Text fontSize="sm" color="gray.500">
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
              </Text>
              <HStack>
                <IconButton size="sm" icon={<FiChevronLeft />} isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} />
                <IconButton size="sm" icon={<FiChevronRight />} isDisabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} />
              </HStack>
            </Flex>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default PaymentList;
